import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { UserPlus, Trash2, Edit, Plus, Check, X, Users, Settings, Loader } from 'lucide-react';

const WorkflowManagementPage = () => {
  const [workflows, setWorkflows] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('workflows');
  
  // Workflow analytics state
  const [workflowStats, setWorkflowStats] = useState(null);
  const [workflowTimeline, setWorkflowTimeline] = useState([]);
  const [timelinePeriod, setTimelinePeriod] = useState('weekly');
  const [timelineDays, setTimelineDays] = useState(90);  
  // Platform switcher uses viewing_tenant_id; empty = All Tenants.
  const tenantId = localStorage.getItem('viewing_tenant_id') || '';  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  
  // Dialog states
  const [isWorkflowDialogOpen, setIsWorkflowDialogOpen] = useState(false);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [isApproverDialogOpen, setIsApproverDialogOpen] = useState(false);
  const [isEditUserDialogOpen, setIsEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => {
    fetchData();
    fetchWorkflowAnalytics();
  }, []);

  useEffect(() => {
    fetchWorkflowTimeline();
  }, [timelinePeriod, timelineDays]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchWorkflows(),
        fetchUsers()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkflowAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await fetch('/api/v1/workflow/statistics/');
      const data = await res.json();
      if (data.status === 'success' && data.statistics) {
        const stats = data.statistics;
        setWorkflowStats({
          total_workflows: stats.total_reviews || 0,
          pending_approval: stats.pending_count || 0,
          approved: stats.approved_count || 0,
          rejected: stats.rejected_count || 0,
          avg_approval_time_hours: stats.avg_review_time_hours || 0
        });
      } else {
        setWorkflowStats({
          total_workflows: 0,
          pending_approval: 0,
          approved: 0,
          rejected: 0,
          avg_approval_time_hours: 0
        });
      }
    } catch (error) {
      console.error('Error fetching workflow statistics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchWorkflowTimeline = async () => {
    try {
      const res = await fetch(`/api/v1/workflow/analytics/timeline/?period=${timelinePeriod}&days=${timelineDays}`);
      const data = await res.json();
      if (data.status === 'success') {
        setWorkflowTimeline(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching workflow timeline:', error);
    }
  };

  const fetchWorkflows = async () => {
    try {
      const res = await fetch('/api/v1/workflow/configurations/');
      const data = await res.json();
      if (data.status === 'success') {
        setWorkflows(data.workflows);
      }
    } catch (error) {
      console.error('Error fetching workflows:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch('/api/v1/workflow/users/');
      const data = await res.json();
      if (data.status === 'success') {
        setUsers(data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchWorkflowDetails = async (workflowId) => {
    try {
      const res = await fetch(`/api/v1/workflow/configurations/${workflowId}/`);
      const data = await res.json();
      if (data.status === 'success') {
        setSelectedWorkflow(data.workflow);
      }
    } catch (error) {
      console.error('Error fetching workflow details:', error);
    }
  };

  const handleCreateWorkflow = async (formData) => {
    try {
      const res = await fetch('/api/v1/workflow/configurations/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchWorkflows();
        setIsWorkflowDialogOpen(false);
      }
    } catch (error) {
      console.error('Error creating workflow:', error);
    }
  };

  const handleCreateUser = async (formData) => {
    try {
      const res = await fetch('/api/v1/workflow/users/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchUsers();
        setIsUserDialogOpen(false);
      }
    } catch (error) {
      console.error('Error creating user:', error);
    }
  };

  const handleAddApprover = async (workflowId, userId, approvalOrder) => {
    try {
      const res = await fetch(`/api/v1/workflow/configurations/${workflowId}/approvers/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, approval_order: approvalOrder })
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchWorkflowDetails(workflowId);
        setIsApproverDialogOpen(false);
      }
    } catch (error) {
      console.error('Error adding approver:', error);
    }
  };

  const handleRemoveApprover = async (workflowId, approverId) => {
    if (!confirm('Are you sure you want to remove this approver?')) return;
    
    try {
      const res = await fetch(`/api/v1/workflow/configurations/${workflowId}/approvers/${approverId}/`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchWorkflowDetails(workflowId);
      }
    } catch (error) {
      console.error('Error removing approver:', error);
    }
  };

  const toggleWorkflowStatus = async (workflowId, currentStatus) => {
    try {
      const res = await fetch(`/api/v1/workflow/configurations/${workflowId}/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !currentStatus })
      });
      const data = await res.json();
      if (data.status === 'success') {
        fetchWorkflows();
      }
    } catch (error) {
      console.error('Error toggling workflow status:', error);
    }
  };

  const handleDeleteWorkflow = async (workflowId, workflowName) => {
    if (!confirm(`Are you sure you want to delete "${workflowName}"? This action cannot be undone.`)) return;
    
    try {
      const res = await fetch(`/api/v1/workflow/configurations/${workflowId}/`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.status === 'success') {
        alert(data.message);
        fetchWorkflows();
      } else if (data.in_use) {
        alert(data.message);
      }
    } catch (error) {
      console.error('Error deleting workflow:', error);
      alert('Failed to delete workflow');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setIsEditUserDialogOpen(true);
  };

  const handleUpdateUser = async (formData) => {
    try {
      const res = await fetch(`/api/v1/workflow/users/${editingUser.id}/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.status === 'success') {
        alert('User updated successfully!');
        fetchUsers();
        setIsEditUserDialogOpen(false);
        setEditingUser(null);
      } else {
        alert(data.message || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!confirm(`Are you sure you want to delete user "${userName}"?`)) return;
    
    try {
      const res = await fetch(`/api/v1/workflow/users/${userId}/`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.status === 'success') {
        alert('User deleted successfully!');
        fetchUsers();
      } else {
        alert(data.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  // ============================================================================
  // Analytics Components
  // ============================================================================

  const WorkflowStatsCard = () => {
    if (analyticsLoading) {
      return (
        <Card>
          <CardHeader>
            <CardTitle>Approval Status</CardTitle>
          </CardHeader>
          <CardContent className="h-48 flex items-center justify-center">
            <Loader className="w-8 h-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardHeader>
          <CardTitle>Approval Status</CardTitle>
          <CardDescription>Workflow instances by status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm">Total Workflows</span>
              <Badge variant="outline">{workflowStats?.total_workflows || 0}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-amber-600">Pending Approval</span>
              <Badge className="bg-amber-100 text-amber-800">{workflowStats?.pending_approval || 0}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-green-600">Approved</span>
              <Badge className="bg-green-100 text-green-800">{workflowStats?.approved || 0}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-red-600">Rejected</span>
              <Badge className="bg-red-100 text-red-800">{workflowStats?.rejected || 0}</Badge>
            </div>
            <hr className="my-3" />
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold">Avg Approval Time</span>
              <span className="font-semibold">{workflowStats?.avg_approval_time_hours || 0}h</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const WorkflowTimelineChart = () => {
    const periodOptions = [
      { value: 'daily', label: 'Daily', defaultDays: 30 },
      { value: 'weekly', label: 'Weekly', defaultDays: 90 },
      { value: 'monthly', label: 'Monthly', defaultDays: 180 },
      { value: 'quarterly', label: 'Quarterly', defaultDays: 365 },
      { value: 'yearly', label: 'Yearly', defaultDays: 730 }
    ];

    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Workflow Approval Timeline</CardTitle>
              <CardDescription>Track approval trends over time</CardDescription>
            </div>
            <div className="flex gap-2">
              {periodOptions.map(opt => (
                <Button
                  key={opt.value}
                  variant={timelinePeriod === opt.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setTimelinePeriod(opt.value);
                    setTimelineDays(opt.defaultDays);
                  }}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Line Chart - Approval Trends */}
            <div>
              <h3 className="text-sm font-semibold mb-3">Approval Trends</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={workflowTimeline}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="approved" stroke="#10b981" strokeWidth={2} name="Approved" />
                  <Line type="monotone" dataKey="rejected" stroke="#ef4444" strokeWidth={2} name="Rejected" />
                  <Line type="monotone" dataKey="in_progress" stroke="#f59e0b" strokeWidth={2} name="In Progress" />
                  <Line type="monotone" dataKey="further_review" stroke="#8b5cf6" strokeWidth={2} name="Further Review" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4 pt-4 border-t">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">
                  {(workflowTimeline || []).reduce((sum, d) => sum + (d.approved || 0), 0)}
                </p>
                <p className="text-xs text-muted-foreground">Total Approved</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">
                  {(workflowTimeline || []).reduce((sum, d) => sum + (d.rejected || 0), 0)}
                </p>
                <p className="text-xs text-muted-foreground">Total Rejected</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-600">
                  {(workflowTimeline || []).reduce((sum, d) => sum + (d.in_progress || 0), 0)}
                </p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">
                  {(workflowTimeline || []).reduce((sum, d) => sum + (d.further_review || 0), 0)}
                </p>
                <p className="text-xs text-muted-foreground">Further Review</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const WorkflowForm = () => {
    const [formData, setFormData] = useState({
      name: '',
      description: '',
      workflow_type: 'report_review',
      require_all_approvals: false
    });

    return (
      <div className="space-y-4">
        <div>
          <Label htmlFor="name">Workflow Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Report Review Workflow"
          />
        </div>
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Describe the purpose of this workflow"
            rows={3}
          />
        </div>
        <div>
          <Label htmlFor="workflow_type">Workflow Type</Label>
          <Select value={formData.workflow_type} onValueChange={(val) => setFormData({ ...formData, workflow_type: val })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="report_review">Report Review</SelectItem>
              <SelectItem value="merchant_approval">Merchant Approval</SelectItem>
              <SelectItem value="data_validation">Data Validation</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="require_all"
            checked={formData.require_all_approvals}
            onChange={(e) => setFormData({ ...formData, require_all_approvals: e.target.checked })}
            className="h-4 w-4"
          />
          <Label htmlFor="require_all">Require all approvers to approve (Sequential)</Label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsWorkflowDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => handleCreateWorkflow(formData)}>Create Workflow</Button>
        </DialogFooter>
      </div>
    );
  };

  const UserForm = () => {
    const [formData, setFormData] = useState({
      email: '',
      full_name: '',
      role: 'Reviewer'
    });

    return (
      <div className="space-y-4">
        <div>
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="user@example.com"
          />
        </div>
        <div>
          <Label htmlFor="full_name">Full Name *</Label>
          <Input
            id="full_name"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            placeholder="John Doe"
          />
        </div>
        <div>
          <Label htmlFor="role">Role</Label>
          <Input
            id="role"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            placeholder="e.g., Senior Reviewer, Manager"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsUserDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => handleCreateUser(formData)}>Add User</Button>
        </DialogFooter>
      </div>
    );
  };

  const EditUserForm = () => {
    const [formData, setFormData] = useState({
      email: editingUser?.email || '',
      full_name: editingUser?.full_name || '',
      role: editingUser?.role || ''
    });

    return (
      <div className="space-y-4">
        <div>
          <Label htmlFor="edit_email">Email *</Label>
          <Input
            id="edit_email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="user@example.com"
            disabled
          />
          <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
        </div>
        <div>
          <Label htmlFor="edit_full_name">Full Name *</Label>
          <Input
            id="edit_full_name"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            placeholder="John Doe"
          />
        </div>
        <div>
          <Label htmlFor="edit_role">Role</Label>
          <Input
            id="edit_role"
            value={formData.role}
            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            placeholder="e.g., Senior Reviewer, Manager"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => {
            setIsEditUserDialogOpen(false);
            setEditingUser(null);
          }}>Cancel</Button>
          <Button onClick={() => handleUpdateUser(formData)}>Update User</Button>
        </DialogFooter>
      </div>
    );
  };

  const ApproverForm = ({ workflowId }) => {
    const [selectedUser, setSelectedUser] = useState('');
    const [approvalOrder, setApprovalOrder] = useState(1);

    return (
      <div className="space-y-4">
        <div>
          <Label htmlFor="user">Select User *</Label>
          <Select value={selectedUser} onValueChange={setSelectedUser}>
            <SelectTrigger>
              <SelectValue placeholder="Choose a user" />
            </SelectTrigger>
            <SelectContent>
              {users.filter(u => u.is_active).map((user) => (
                <SelectItem key={user.id} value={user.id.toString()}>
                  {user.full_name} ({user.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="order">Approval Order</Label>
          <Input
            id="order"
            type="number"
            min="1"
            value={approvalOrder}
            onChange={(e) => setApprovalOrder(parseInt(e.target.value))}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Lower numbers will be processed first (for sequential workflows)
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsApproverDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => handleAddApprover(workflowId, parseInt(selectedUser), approvalOrder)}>
            Add Approver
          </Button>
        </DialogFooter>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8 max-w-[1400px]">
        <Card>
          <CardHeader>
            <CardTitle>Loading Workflow Management...</CardTitle>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6 max-w-[1400px]">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Workflow Management</h1>
          <p className="text-muted-foreground">Configure review workflows and approvers</p>
        </div>
        <Button onClick={fetchData}>Refresh Data</Button>
      </div>

      {/* Workflow Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <WorkflowStatsCard />
        <div className="lg:col-span-2">
          <WorkflowTimelineChart />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="workflows">
            <Settings className="w-4 h-4 mr-2" />
            Workflows
          </TabsTrigger>
          <TabsTrigger value="users">
            <Users className="w-4 h-4 mr-2" />
            Users
          </TabsTrigger>
        </TabsList>

        {/* Workflows Tab */}
        <TabsContent value="workflows">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Workflow Configurations</CardTitle>
                  <CardDescription>Manage review and approval workflows</CardDescription>
                </div>
                <Dialog open={isWorkflowDialogOpen} onOpenChange={setIsWorkflowDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Workflow
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Workflow</DialogTitle>
                      <DialogDescription>Configure a new approval workflow</DialogDescription>
                    </DialogHeader>
                    <WorkflowForm />
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Workflow Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Approvers</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workflows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No workflows configured
                      </TableCell>
                    </TableRow>
                  ) : (
                    workflows.map((workflow) => (
                      <TableRow key={workflow.id}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{workflow.name}</div>
                            {workflow.description && (
                              <div className="text-xs text-muted-foreground">{workflow.description}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{workflow.workflow_type.replace('_', ' ')}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{workflow.approver_count} approvers</Badge>
                        </TableCell>
                        <TableCell>
                          {workflow.require_all_approvals ? (
                            <Badge className="bg-blue-500">Sequential</Badge>
                          ) : (
                            <Badge variant="outline">Parallel</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {workflow.is_active ? (
                            <Badge className="bg-green-500">Active</Badge>
                          ) : (
                            <Badge variant="destructive">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                fetchWorkflowDetails(workflow.id);
                                setActiveTab('workflows');
                              }}
                              title="Edit Workflow"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant={workflow.is_active ? "destructive" : "default"}
                              onClick={() => toggleWorkflowStatus(workflow.id, workflow.is_active)}
                              title={workflow.is_active ? "Deactivate" : "Activate"}
                            >
                              {workflow.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteWorkflow(workflow.id, workflow.name)}
                              title="Delete Workflow"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {/* Selected Workflow Details */}
              {selectedWorkflow && (
                <div className="mt-6 border-t pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">
                      Approvers for: {selectedWorkflow.name}
                    </h3>
                    <Dialog open={isApproverDialogOpen} onOpenChange={setIsApproverDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm">
                          <UserPlus className="w-4 h-4 mr-2" />
                          Add Approver
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add Approver</DialogTitle>
                          <DialogDescription>Add a user as an approver to this workflow</DialogDescription>
                        </DialogHeader>
                        <ApproverForm workflowId={selectedWorkflow.id} />
                      </DialogContent>
                    </Dialog>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Order</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Permissions</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedWorkflow.approvers?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground">
                            No approvers assigned
                          </TableCell>
                        </TableRow>
                      ) : (
                        selectedWorkflow.approvers?.map((approver) => (
                          <TableRow key={approver.id}>
                            <TableCell>
                              <Badge variant="outline">{approver.approval_order}</Badge>
                            </TableCell>
                            <TableCell>{approver.full_name}</TableCell>
                            <TableCell className="text-xs">{approver.email}</TableCell>
                            <TableCell>
                              <Badge variant="secondary">{approver.role}</Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                {approver.can_approve && <Badge className="bg-green-600 text-xs">Approve</Badge>}
                                {approver.can_reject && <Badge className="bg-red-600 text-xs">Reject</Badge>}
                                {approver.can_request_review && <Badge className="bg-yellow-600 text-xs">Request Review</Badge>}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleRemoveApprover(selectedWorkflow.id, approver.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Workflow Users</CardTitle>
                  <CardDescription>Manage users who can participate in workflows</CardDescription>
                </div>
                <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add User
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New User</DialogTitle>
                      <DialogDescription>Add a user who can participate in workflow approvals</DialogDescription>
                    </DialogHeader>
                    <UserForm />
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            
            {/* Edit User Dialog */}
            <Dialog open={isEditUserDialogOpen} onOpenChange={setIsEditUserDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit User</DialogTitle>
                  <DialogDescription>Update user information</DialogDescription>
                </DialogHeader>
                {editingUser && <EditUserForm />}
              </DialogContent>
            </Dialog>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.full_name}</TableCell>
                        <TableCell className="text-xs">{user.email}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{user.role}</Badge>
                        </TableCell>
                        <TableCell>
                          {user.is_active ? (
                            <Badge className="bg-green-500">Active</Badge>
                          ) : (
                            <Badge variant="destructive">Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditUser(user)}
                              title="Edit User"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteUser(user.id, user.full_name)}
                              title="Delete User"
                              disabled={user.email === 'system@mcc.internal'}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WorkflowManagementPage;
